// src/pages/GroupMasterPage.jsx
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { motion } from "framer-motion";
import { Plus, Edit2, Trash2, X, Search } from "lucide-react";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";

export default function GroupMasterPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // modal / form states
  const [isOpen, setIsOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDiscount, setFormDiscount] = useState(0);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // table state
  const [queryText, setQueryText] = useState("");
  const [sortBy, setSortBy] = useState({ key: "name", dir: "asc" });
  const [page, setPage] = useState(1);
  const perPage = 8;

  // derived vars
  const totalPages = Math.max(
    1,
    Math.ceil(
      (groups || []).filter((g) =>
        g.name?.toLowerCase().includes(queryText.toLowerCase())
      ).length / perPage
    )
  );
  const pageSafe = Math.min(page, totalPages);

  // live listener
  useEffect(() => {
    const col = collection(db, "groups");
    const unsub = onSnapshot(
      col,
      (snap) => {
        const arr = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: data.name || "",
            nameLower: data.nameLower || (data.name || "").toLowerCase(),
            discount: Number(data.discount || 0),
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
          };
        });
        setGroups(arr);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // keyboard shortcuts: n => new, / => focus search, esc => close modal
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "n") {
        openCreate();
      }
      if (e.key === "/") {
        const el = document.getElementById("group-search");
        if (el) {
          e.preventDefault();
          el.focus();
        }
      }
      if (e.key === "Escape") {
        closeModal();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // helpers
  const closeModal = () => {
    setIsOpen(false);
    setFormName("");
    setFormDiscount(0);
    setEditId(null);
    setError("");
  };

  const openCreate = () => {
    setFormName("");
    setFormDiscount(0);
    setEditId(null);
    setError("");
    setIsOpen(true);
  };

  const openEdit = (g) => {
    setFormName(g.name || "");
    setFormDiscount(Number(g.discount || 0));
    setEditId(g.id);
    setError("");
    setIsOpen(true);
  };

  const isDuplicateName = async (value, excludingId = null) => {
    const q = query(
      collection(db, "groups"),
      where("nameLower", "==", value.trim().toLowerCase())
    );
    const snap = await getDocs(q);
    if (snap.empty) return false;
    if (excludingId) {
      const others = snap.docs.filter((d) => d.id !== excludingId);
      return others.length > 0;
    }
    return true;
  };

  const handleSave = async () => {
    setError("");
    const trimmed = formName.trim();
    if (!trimmed) return setError("Group name is required");

    // validate discount
    const discNum = Number(formDiscount || 0);
    if (isNaN(discNum) || discNum < 0 || discNum > 100) {
      return setError("Discount must be a number between 0 and 100");
    }

    setSaving(true);
    try {
      const duplicate = await isDuplicateName(trimmed, editId);
      if (duplicate) {
        setError("A group with this name already exists");
        setSaving(false);
        return;
      }

      const payload = {
        name: trimmed,
        nameLower: trimmed.toLowerCase(),
        discount: discNum,
        updatedAt: new Date(),
      };

      if (editId) {
        await updateDoc(doc(db, "groups", editId), payload);
      } else {
        await addDoc(collection(db, "groups"), {
          ...payload,
          createdAt: new Date(),
        });
      }

      closeModal();
    } catch (err) {
      console.error(err);
      setError("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Confirm delete this group?");
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "groups", id));
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  // derived list: filter, sort, paginate
  const filtered = (groups || []).filter((g) =>
    g.name?.toLowerCase().includes(queryText.toLowerCase())
  );

  const sorted = filtered.slice().sort((a, b) => {
    const A = (a[sortBy.key] || "").toString().toLowerCase();
    const B = (b[sortBy.key] || "").toString().toLowerCase();
    if (A < B) return sortBy.dir === "asc" ? -1 : 1;
    if (A > B) return sortBy.dir === "asc" ? 1 : -1;
    return 0;
  });

  const paged = sorted.slice((pageSafe - 1) * perPage, pageSafe * perPage);

  const toggleSort = (key) => {
    if (sortBy.key === key) setSortBy({ key, dir: sortBy.dir === "asc" ? "desc" : "asc" });
    else setSortBy({ key, dir: "asc" });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Top MenuBar - full width */}
      <div className="w-full shadow z-50">
        <MenuBar />
      </div>

      {/* HeaderBar - below MenuBar */}
      <div className="w-full border-b shadow-sm z-40">
        <HeaderBar />
      </div>

      {/* Content area - scrollable */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-slate-800">Group Master</h1>
              <p className="text-sm text-slate-500">
                  Manage groups. Employee commission: Masters →{" "}
                <a href="/group-commission-settings" className="text-indigo-600 hover:underline">
                  Party discount / commission
                </a>
                (select party, set group %).
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  id="group-search"
                  value={queryText}
                  onChange={(e) => {
                    setQueryText(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search groups... ( / to focus )"
                  className="pl-9 pr-3 py-2 border rounded-md w-64 bg-white focus:ring-2 focus:ring-indigo-300"
                />
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
              </div>

              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-md shadow-md hover:bg-indigo-700"
                title="New (n)"
              >
                <Plus className="w-4 h-4" /> New
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Table header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="text-sm text-slate-600">{groups.length} total groups</div>
              <div className="flex items-center gap-4">
                <div className="text-sm text-slate-600">Sort:</div>
                <button
                  onClick={() => toggleSort("name")}
                  className={`px-2 py-1 rounded text-sm ${sortBy.key === "name" ? "bg-slate-100" : ""}`}
                >
                  Name {sortBy.key === "name" ? (sortBy.dir === "asc" ? "▲" : "▼") : ""}
                </button>
                <button
                  onClick={() => toggleSort("discount")}
                  className={`px-2 py-1 rounded text-sm ${sortBy.key === "discount" ? "bg-slate-100" : ""}`}
                >
                  Discount % {sortBy.key === "discount" ? (sortBy.dir === "asc" ? "▲" : "▼") : ""}
                </button>
              </div>
            </div>

            {/* Table body */}
            <div className="p-4">
              {loading ? (
                // skeleton
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-10 bg-gray-100 rounded" />
                  ))}
                </div>
              ) : (
                <div>
                  {paged.length === 0 ? (
                    <div className="py-12 text-center text-slate-500">
                      <p className="mb-3">No groups found.</p>
                      <button onClick={openCreate} className="inline-flex items-center gap-2 text-indigo-600">
                        <Plus className="w-4 h-4" /> Create first group
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto text-sm">
                        <thead>
                          <tr className="text-left text-slate-600 border-b">
                            <th className="py-2 px-3">Name</th>
                            <th className="py-2 px-3 text-right">Discount %</th>
                            <th className="py-2 px-3 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paged.map((g) => (
                            <tr key={g.id} className="hover:bg-slate-50">
                              <td className="py-3 px-3 align-middle">
                                <div className="font-medium text-slate-800">{g.name}</div>
                                <div className="text-xs text-slate-400">
                                  {g.createdAt
                                    ? new Date(g.createdAt.seconds ? g.createdAt.seconds * 1000 : g.createdAt).toLocaleString()
                                    : ""}
                                </div>
                              </td>

                              <td className="py-3 px-3 align-middle text-right">
                                <div className="font-medium">{Number(g.discount || 0).toFixed(2)}%</div>
                              </td>

                              <td className="py-3 px-3 text-right">
                                <div className="inline-flex gap-2">
                                  <button
                                    onClick={() => openEdit(g)}
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded border hover:bg-yellow-50"
                                  >
                                    <Edit2 className="w-4 h-4" /> Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(g.id)}
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded border bg-rose-50 hover:bg-rose-100"
                                  >
                                    <Trash2 className="w-4 h-4 text-rose-600" /> Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Showing {paged.length === 0 ? 0 : Math.min((pageSafe - 1) * perPage + 1, sorted.length)} - {Math.min(pageSafe * perPage, sorted.length)} of {sorted.length}
                    </div>
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => setPage(1)} disabled={pageSafe === 1} className="px-2 py-1 border rounded">First</button>
                      <button onClick={() => setPage(Math.max(1, pageSafe - 1))} disabled={pageSafe === 1} className="px-2 py-1 border rounded">Prev</button>
                      <div className="px-3 py-1 border rounded">Page {pageSafe} / {totalPages}</div>
                      <button onClick={() => setPage(Math.min(totalPages, pageSafe + 1))} disabled={pageSafe === totalPages} className="px-2 py-1 border rounded">Next</button>
                      <button onClick={() => setPage(totalPages)} disabled={pageSafe === totalPages} className="px-2 py-1 border rounded">Last</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Modal - add / edit */}
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={closeModal} />

          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-5 z-10">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">{editId ? "Edit Group" : "New Group"}</h3>
                <p className="text-sm text-slate-500">
                  Name + product discount %. For employee commission:{" "}
                  <a href="/group-commission-settings" className="text-indigo-600 hover:underline">
                    Party discount / commission
                  </a>
                  .
                </p>
              </div>
              <button onClick={closeModal} aria-label="close" className="p-2 rounded hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-sm text-slate-700 mb-1">Name</label>
                <input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                    if (e.key === "Escape") closeModal();
                  }}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm text-slate-700 mb-1">Product discount % (0-100)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formDiscount}
                  onChange={(e) => setFormDiscount(e.target.value)}
                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                    if (e.key === "Escape") closeModal();
                  }}
                />
                <div className="text-xs text-slate-400 mt-1">
                  Used for product rates. If employee commission is not set separately, this % is the starting
                  commission rate.
                </div>
              </div>

              {error && <div className="text-rose-600 text-sm">{error}</div>}
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={closeModal} className="px-3 py-2 rounded border">Cancel</button>
              <button onClick={handleSave} disabled={saving} className={`px-4 py-2 rounded ${saving ? "bg-gray-300" : "bg-indigo-600 text-white"}`}>
                {saving ? "Saving..." : editId ? "Update" : "Create"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}
